import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Award, Download, GraduationCap, BadgeCheck, ScanLine, ShieldX } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";

async function downloadCert(cert) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${API}/certificates/${cert.id}/download?auth=${token}`, {
    headers: { Authorization: `Bearer ${token}` }, credentials: "include",
  });
  if (!res.ok) throw new Error("fail");
  const blob = await res.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${cert.cert_number}.pdf`;
  document.body.appendChild(link); link.click(); link.remove();
}

const PALETTE = ["from-emerald-50 to-teal-50 border-emerald-100", "from-amber-50 to-orange-50 border-amber-100", "from-sky-50 to-indigo-50 border-sky-100", "from-rose-50 to-pink-50 border-rose-100"];

function CertCard({ cert, idx, showStudent }) {
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    setBusy(true);
    try { await downloadCert(cert); toast.success("تم تنزيل الشهادة"); }
    catch { toast.error("تعذّر تنزيل الشهادة"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`cert-${cert.id}`} className={`rounded-2xl border bg-gradient-to-br ${PALETTE[idx % PALETTE.length]} p-5 soft-shadow`}>
      <div className="flex items-start justify-between">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/70 text-navy soft-shadow">
          <GraduationCap className="h-6 w-6" />
        </div>
        <span className="flex items-center gap-1 rounded-full bg-white/70 px-3 py-1 text-xs font-semibold text-emerald-700">
          <BadgeCheck className="h-3.5 w-3.5" /> {cert.grade_letter}
        </span>
      </div>
      <h3 className="mt-3 font-heading text-lg font-bold text-navy">{cert.program_name}</h3>
      {showStudent && <p className="mt-0.5 text-sm font-semibold text-slate-600">{cert.student_name}</p>}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
        <span>النتيجة: {cert.final}%</span>
        <span>الساعات: {cert.attended_hours}/{cert.total_hours}</span>
      </div>
      <p className="mt-1 font-mono text-xs text-slate-400">{cert.cert_number}</p>
      <Button data-testid={`cert-download-${cert.id}`} onClick={dl} disabled={busy} className="mt-4 w-full rounded-xl bg-navy hover:bg-navy/90">
        <Download className="me-2 h-4 w-4" /> {busy ? "..." : "تحميل الشهادة (PDF)"}
      </Button>
    </div>
  );
}

function StudentCertificates() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/certificates/mine").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  if (items === null) return <PageLoader />;
  return (
    <>
      <PageHeader title="شهاداتي" subtitle="شهادات إتمام البرامج التي اجتزتها بنجاح — تُصدر تلقائيًا عند اكتمال البرنامج" />
      {items.length === 0 ? (
        <EmptyState icon={Award} title="لا توجد شهادات بعد" desc="ستظهر شهادتك هنا تلقائيًا عند إتمامك برنامجًا بنجاح واستيفاء نسبة الحضور المطلوبة." />
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((c, i) => <CertCard key={c.id} cert={c} idx={i} />)}
        </div>
      )}
    </>
  );
}

function VerifyScanner() {
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const scannerRef = useRef(null);

  const extractCode = (txt) => {
    try { const p = (txt || "").split("?")[0].split("/").filter(Boolean); return p[p.length - 1]; }
    catch { return txt; }
  };
  const check = async (code) => {
    try { const r = await api.get(`/certificates/verify/${code}`); setResult(r.data); }
    catch { setResult({ valid: false }); }
  };
  const stop = async () => {
    try { if (scannerRef.current) { await scannerRef.current.stop(); scannerRef.current.clear(); scannerRef.current = null; } } catch (e) {}
    setScanning(false);
  };
  const start = async () => {
    setResult(null); setScanning(true);
    setTimeout(async () => {
      try {
        const s = new Html5Qrcode("cert-qr-reader");
        scannerRef.current = s;
        await s.start({ facingMode: "environment" }, { fps: 10, qrbox: 250 },
          async (decoded) => { await stop(); await check(extractCode(decoded)); }, () => {});
      } catch (e) { toast.error("تعذّر تشغيل الكاميرا"); setScanning(false); }
    }, 200);
  };
  const close = async () => { await stop(); setOpen(false); setResult(null); };

  return (
    <>
      <Button data-testid="cert-verify-scan-btn" variant="outline" onClick={() => setOpen(true)} className="rounded-xl border-navy/20 text-navy">
        <ScanLine className="me-2 h-4 w-4" /> التحقق من شهادة (مسح QR)
      </Button>
      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-navy">التحقق من صحة شهادة</DialogTitle></DialogHeader>
          {!result ? (scanning ? (
            <div>
              <div id="cert-qr-reader" className="overflow-hidden rounded-xl" style={{ width: "100%" }} />
              <Button data-testid="cert-scan-stop" onClick={stop} variant="outline" className="mt-3 w-full rounded-xl border-red-200 text-red-500">إيقاف المسح</Button>
            </div>
          ) : (
            <div className="py-3 text-center">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-navy/5 text-navy"><ScanLine className="h-10 w-10" /></div>
              <p className="mt-3 text-sm text-slate-500">امسح رمز QR الموجود أسفل الشهادة للتحقق من صحتها فورًا.</p>
              <Button data-testid="cert-scan-start" onClick={start} className="mt-4 w-full rounded-xl bg-greenx hover:bg-greenx-light">فتح الكاميرا</Button>
            </div>
          )) : result.valid ? (
            <div data-testid="scan-result-valid" className="py-1 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100"><BadgeCheck className="h-8 w-8 text-emerald-600" /></div>
              <h3 className="mt-2 font-heading text-lg font-bold text-emerald-700">شهادة صحيحة ✓</h3>
              <div className="mt-3 space-y-1.5 rounded-xl bg-slate-50 p-4 text-start text-sm">
                <p><span className="text-slate-400">الطالب: </span><b className="text-navy">{result.student_name}</b></p>
                <p><span className="text-slate-400">البرنامج: </span>{result.program_name}</p>
                <p><span className="text-slate-400">التقدير: </span>{result.grade_letter} ({result.final}%)</p>
                <p><span className="text-slate-400">رقم الشهادة: </span><span className="font-mono">{result.cert_number}</span></p>
                <p><span className="text-slate-400">تاريخ الإصدار: </span>{result.issued_date}</p>
              </div>
              <Button onClick={() => setResult(null)} variant="outline" className="mt-3 w-full rounded-xl">مسح شهادة أخرى</Button>
            </div>
          ) : (
            <div data-testid="scan-result-invalid" className="py-3 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-rose-100"><ShieldX className="h-8 w-8 text-rose-600" /></div>
              <h3 className="mt-2 font-heading text-lg font-bold text-rose-700">شهادة غير صالحة</h3>
              <p className="mt-1 text-sm text-slate-500">لم يتم العثور على شهادة بهذا الرمز.</p>
              <Button onClick={() => setResult(null)} variant="outline" className="mt-3 w-full rounded-xl">إعادة المحاولة</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function AdminCertificates() {
  const [items, setItems] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [pid, setPid] = useState("");
  const [issuing, setIssuing] = useState(false);
  const load = () => api.get("/certificates").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/programs").then((r) => setPrograms(r.data.filter((p) => p.status === "finished"))).catch(() => setPrograms([]));
  }, []);
  const issue = async () => {
    if (!pid) { toast.error("اختر برنامجًا منتهيًا"); return; }
    setIssuing(true);
    try { const r = await api.post(`/certificates/issue-program/${pid}`); toast.success(r.data.message); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setIssuing(false); }
  };
  if (items === null) return <PageLoader />;
  return (
    <>
      <PageHeader title="الشهادات" subtitle="إصدار ومتابعة شهادات إتمام البرامج" />
      <div className="mb-5"><VerifyScanner /></div>
      <div className="mb-6 rounded-2xl border border-slate-100 bg-card p-5 soft-shadow">
        <p className="mb-3 text-sm font-semibold text-navy">إصدار شهادات لبرنامج منتهٍ</p>
        <div className="flex flex-wrap items-end gap-3">
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="cert-program-select" className="w-72 rounded-lg"><SelectValue placeholder="اختر برنامجًا منتهيًا" /></SelectTrigger>
            <SelectContent>
              {programs.length === 0 ? <SelectItem value="__none" disabled>لا توجد برامج منتهية</SelectItem>
                : programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="cert-issue-btn" onClick={issue} disabled={issuing || !pid} className="rounded-xl bg-greenx hover:bg-greenx-light">
            <Award className="me-2 h-4 w-4" /> {issuing ? "جارٍ الإصدار..." : "إصدار الشهادات"}
          </Button>
        </div>
        <p className="mt-2 text-xs text-slate-400">تُصدر الشهادات فقط للطلاب الناجحين (النتيجة ≥ 60% ونسبة الحضور المطلوبة).</p>
      </div>
      {items.length === 0 ? <EmptyState icon={Award} title="لا توجد شهادات صادرة" /> : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((c, i) => <CertCard key={c.id} cert={c} idx={i} showStudent />)}
        </div>
      )}
    </>
  );
}

export default function Certificates() {
  const { user } = useAuth();
  return user.role === "student" ? <StudentCertificates /> : <AdminCertificates />;
}
